import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolSite,
  ProtocolWarning,
} from "../types";

/**
 * Luveris — lutropin alfa, the recombinant LH a woman with almost none of her
 * own injects every day alongside her FSH, and the kit whose solvent comes in
 * a second vial rather than a syringe.
 *
 * ### One label, two languages, one procedure
 *
 * The EMA product information carries the same leaflet in English and in
 * French, and the two agree sentence for sentence: wash, lay out the two
 * vials and the two needles; draw air to the 1 mL mark, push it into the
 * solvent vial, draw all the solvent up; inject it through the stopper of
 * the powder vial, swirl without removing the syringe, never shake; clear
 * and no particles; draw it back; change to the fine needle and flick the
 * bubbles up; tummy or front of thigh as the doctor showed; a firm pinch
 * and a dart at 45° to 90°; never into a vein; all of it, as long as it
 * takes; out at once, a swab in a circle; glass and needles to the sharps
 * container. The English copy is the English leaflet's and the French the
 * French notice's — the first walk here with that luxury since Elonva.
 *
 * ### The dose is vials
 *
 * One vial is 75 IU and the usual dose; the leaflet allows up to three vials
 * of powder in the one millilitre of solvent, drawn back and re-injected
 * into the next vial. So the dose is said where the vials are chained, and
 * the guard wants units with no strength list. The leaflet also lets her
 * dissolve a follitropin alfa powder in the same syringe; the walk does not,
 * because the Gonal-f powder has its own leaflet and its own walk.
 *
 * ### Until now a name on another medication
 *
 * The content aliased Luveris, lutropin alfa and r-hLH on the Pergoveris
 * entry, which is follitropin alfa with lutropin alfa in one vial and not
 * this. They are now this entry's own, and the Pergoveris walk no longer
 * has to say it does not answer to them.
 *
 * Restated from `content/data/medications/gonadotropins.json` (the `Luveris`
 * entry, written from these two leaflets).
 */

const LUVERIS_BRANDS = [
  { name: "Luveris", markets: ["EU", "GB"] },
] as const satisfies readonly ProtocolBrand[];

/** The leaflet's two sentences; no hour, and the doctor is the answer. */
const MISSED_DOSE: ProtocolWarning = {
  id: "luveris.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - Do not use a double dose to make up for a forgotten dose. Please contact your doctor.",
    fr: "Dose oubliée - Ne prenez pas de dose double pour compenser la dose que vous avez oubliée de prendre. Consultez votre médecin.",
  },
  sourceSegment:
    "⚠ Missed dose - Do not use a double dose to make up for a forgotten dose. Please contact your doctor.",
};

/**
 * The leaflet's own serious-side-effect lines: "contact your doctor straight
 * away", with the two signs it names. Breathing is not among them.
 */
const OHSS: ProtocolWarning = {
  id: "luveris.ohss",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Hyperstimulation - Contact your doctor straight away if you have lower abdominal pain together with nausea or vomiting: your ovaries may have over-reacted to the treatment, and your doctor will need to examine you as soon as possible.",
    fr: "Hyperstimulation - Contactez immédiatement votre médecin en cas de douleurs dans le bas de l'abdomen accompagnées de nausées ou de vomissements : vos ovaires peuvent avoir réagi de manière excessive au traitement, et votre médecin devra vous examiner dès que possible.",
  },
  sourceSegment:
    "⚠ Hyperstimulation - Contact your doctor straight away if you have lower abdominal pain together with nausea or vomiting: your ovaries may have over-reacted to the treatment, and your doctor will need to examine you as soon as possible.",
};

/** The leaflet's allergic-reaction line, routed to emergency as everywhere here. */
const ALLERGIC_REACTION: ProtocolWarning = {
  id: "luveris.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - A rash, red skin, hives or swelling of your face with difficulty breathing can be serious and need emergency services straight away.",
    fr: "Aide urgente - Une éruption cutanée, une rougeur, de l'urticaire ou un gonflement du visage accompagné de difficultés respiratoires peuvent être graves et nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - A rash, red skin, hives or swelling of your face with difficulty breathing can be serious and need emergency services straight away.",
};

/** "e.g. tummy, front of thigh", already advised by the doctor — so she is asked. */
const SITES: readonly ProtocolSite[] = [
  {
    id: "abdomen",
    label: { en: "Abdomen", fr: "Ventre" },
    copy: {
      en: "Clean the site - Inject the solution immediately, where your doctor or nurse advised you: here, on your abdomen. Wipe the chosen area with an alcohol swab.",
      fr: "Désinfecter le site - Injectez immédiatement la solution, là où votre médecin ou votre infirmière vous l'a indiqué : ici, sur le ventre. Désinfectez la peau au niveau du site d'injection choisi avec un coton imbibé d'alcool.",
    },
  },
  {
    id: "thigh",
    label: { en: "Front of thigh", fr: "Dessus de la cuisse" },
    copy: {
      en: "Clean the site - Inject the solution immediately, where your doctor or nurse advised you: here, on the front of your thigh. Wipe the chosen area with an alcohol swab.",
      fr: "Désinfecter le site - Injectez immédiatement la solution, là où votre médecin ou votre infirmière vous l'a indiqué : ici, sur le dessus de la cuisse. Désinfectez la peau au niveau du site d'injection choisi avec un coton imbibé d'alcool.",
    },
  },
];

export const LUVERIS_PROTOCOL: InjectionProtocol = {
  medicationSlug: "luveris",
  brands: LUVERIS_BRANDS,
  // Not "lutropin alfa": the name is a substring of Pergoveris's own alias,
  // "follitropin alfa lutropin alfa", and a prescription must answer to one
  // medication. The French spelling and the abbreviation are unambiguous.
  aliases: ["Lutropine alfa", "r-hLH"],
  indication: "stimulation",
  // 75 IU per vial, and a dose may be two or three vials in one syringe. No
  // strength list; the unit is the only thing the guard can hold her to.
  dose: { unit: "IU" },
  form: "vial",
  prescriptionForms: ["lyophilized_powder"],
  route: "subcutaneous",
  version: "2026-10-05.1",
  derivedFrom: "content/data/medications/gonadotropins.json#lutropin-alfa",
  sites: SITES,
  steps: [
    {
      id: "luveris.vial.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Wash your hands; it is important that your hands and the items you use are as clean as possible. Find a clean area and lay out everything: one vial of {brand}, one vial of solvent, two alcohol swabs, one syringe, one reconstitution needle for dissolving the powder in the solvent, a fine-bore needle for subcutaneous injection, and a sharps container for the safe disposal of glass and needles. Each vial is for single use only; {brand} is usually used every day for up to three weeks together with your FSH injections, as your doctor decided.",
        fr: "Préparer - Lavez-vous les mains ; il faut absolument que vos mains et tout le matériel que vous utilisez soient aussi propres que possible. Trouvez une surface propre et placez-y tout le matériel : un flacon de {brand}, un flacon de solvant, deux cotons imbibés d'alcool, une seringue, une aiguille de reconstitution pour dissoudre la poudre dans le solvant, une aiguille fine pour l'injection sous-cutanée, et un collecteur conçu pour éliminer en toute sécurité le verre et les aiguilles. Chaque flacon est à usage unique ; {brand} est généralement utilisé tous les jours pendant trois semaines maximum, en même temps que vos injections de FSH, selon la décision de votre médecin.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      // The solvent is in a vial, not a syringe: air in first, then the
      // solvent out, as the leaflet draws it.
      id: "luveris.vial.draw-up-the-solvent",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Draw up the solvent - Remove the protective cap from the solvent vial. Attach the reconstitution needle to the syringe and draw up some air into the syringe by pulling the plunger to approximately the 1 mL mark. Then insert the needle into the vial, push the plunger to expel the air, turn the vial upside down and gently draw up all the solvent. Carefully set the syringe down on the work surface, taking care not to touch the needle.",
        fr: "Prélever le solvant - Enlevez l'opercule protecteur du flacon de solvant. Fixez l'aiguille de reconstitution à la seringue et aspirez un peu d'air dans la seringue en tirant le piston à peu près jusqu'à la marque de 1 mL. Introduisez ensuite l'aiguille dans le flacon, poussez le piston pour expulser l'air, retournez le flacon la tête en bas et aspirez doucement tout le solvant. Reposez précautionneusement la seringue sur la surface de travail, en veillant bien à ne pas toucher l'aiguille.",
      },
    },
    {
      // The chain of vials is the leaflet's ("up to 3 containers of powder
      // may be dissolved in 1 mL of solvent"); the follitropin alfa it also
      // allows in the same syringe is not, see the file comment.
      id: "luveris.vial.reconstitute",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Reconstitute - Remove the protective cap from the {brand} powder vial, pick up your syringe and slowly inject the solvent into the vial through the stopper. Swirl gently without removing the syringe; do not shake. Once the powder has dissolved, which usually happens immediately, check that the solution is clear and does not contain any particles, then turn the vial upside down and gently draw the solution back into the syringe. If your prescribed dose{dose} is more than one vial, re-inject the solution into the next vial of powder and draw it back once dissolved: up to three vials of powder may be dissolved in the same solvent. Do not use the solution if it is not clear.",
        fr: "Reconstituer - Enlevez l'opercule protecteur du flacon de poudre de {brand}, prenez votre seringue et injectez lentement le solvant dans le flacon à travers le bouchon. Remuez très doucement, sans enlever la seringue, sans agiter. Une fois que la poudre est dissoute, ce qui se produit habituellement immédiatement, vérifiez que la solution obtenue est limpide et ne contient pas de particules, puis retournez le flacon la tête en bas et aspirez doucement la solution dans la seringue. Si votre dose prescrite{dose} est de plus d'un flacon, réinjectez la solution dans le flacon de poudre suivant et aspirez-la de nouveau une fois dissoute : un maximum de trois flacons de poudre peut être dissous dans le même solvant. N'injectez pas la solution si elle n'est pas limpide.",
      },
    },
    {
      id: "luveris.vial.change-the-needle",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Change the needle - Replace the reconstitution needle with the fine-bore needle. If you see air bubbles in the syringe, hold the syringe with the needle pointing upwards and gently flick it until all the air collects at the top, then gently push the plunger until the air bubbles are gone.",
        fr: "Changer l'aiguille - Retirez l'aiguille utilisée pour la reconstitution et remplacez-la par l'aiguille fine. Si vous voyez des bulles d'air dans votre seringue, tenez-la verticalement, l'aiguille dirigée vers le haut, puis tapotez doucement la seringue de façon à ce que les bulles d'air remontent, et poussez très doucement sur le piston jusqu'à ce qu'elles aient disparu.",
      },
    },
    {
      id: "luveris.vial.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: SITES[0]!.copy,
    },
    {
      // "45° to 90°" is the leaflet's; the figure keeps its right-angle mark.
      id: "luveris.vial.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Firmly pinch the skin together and insert the needle at a 45° to 90° angle using a dart-like motion. Inject under the skin, as you were taught; do not inject directly into a vein. Inject the solution by pushing gently on the plunger, taking as much time as you need to inject all of it. Then immediately withdraw the needle and clean the skin with an alcohol swab using a circular motion.",
        fr: "Injecter - Pincez fermement la peau entre deux doigts et enfoncez l'aiguille d'un coup sec, avec un angle de 45° à 90°, comme si vous enfonciez une fléchette. Effectuez l'injection sous la peau, comme on vous l'a appris ; n'injectez jamais directement dans une veine. Pour injecter la solution, poussez doucement sur le piston, en prenant tout le temps nécessaire pour injecter toute la solution. Retirez ensuite immédiatement l'aiguille et frottez avec un coton imbibé d'alcool au point d'injection, en faisant des mouvements circulaires.",
      },
      warnings: [ALLERGIC_REACTION],
    },
    {
      id: "luveris.vial.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Immediately discard all the needles and the empty glass vials in your sharps container; any unused solution must be discarded. Do not keep any of it: the medicine must be given immediately after dissolving the powder.",
        fr: "Après l'injection - Jetez immédiatement toutes les aiguilles et les flacons en verre vides dans votre collecteur ; toute solution non utilisée doit être jetée. N'en conservez rien : le médicament doit être administré immédiatement après dissolution de la poudre.",
      },
      warnings: [OHSS],
    },
  ],
};
